import React, { useRef, useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const CreditPreview = ({ credits, previewStyle, onStyleChange }) => {
  const canvasRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);

  const styleOptions = [
    { value: 'title_card', label: 'Title Card Style' },
    { value: 'end_crawl', label: 'End Crawl Style' },
    { value: 'modern_minimal', label: 'Modern Minimal' },
    { value: 'classic_hollywood', label: 'Classic Hollywood' },
    { value: 'indie_film', label: 'Indie Film Style' }
  ];

  const fontOptions = [
    { value: 'inter', label: 'Inter (Modern)' },
    { value: 'times', label: 'Times New Roman (Classic)' },
    { value: 'helvetica', label: 'Helvetica (Clean)' },
    { value: 'garamond', label: 'Garamond (Elegant)' }
  ];

  const [customization, setCustomization] = useState({
    font: 'inter',
    fontSize: 24,
    lineHeight: 1.5,
    textColor: '#FFFFFF',
    backgroundColor: '#000000',
    animationSpeed: 1
  });

  useEffect(() => {
    renderPreview();
  }, [credits, previewStyle, customization, currentFrame]);

  const renderPreview = () => {
    const canvas = canvasRef?.current;
    if (!canvas) return;

    const ctx = canvas?.getContext('2d');
    const { width, height } = canvas;

    // Clear canvas
    ctx.fillStyle = customization?.backgroundColor;
    ctx?.fillRect(0, 0, width, height);

    // Set text properties
    ctx.fillStyle = customization?.textColor;
    ctx.font = `${customization?.fontSize}px ${customization?.font}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (previewStyle === 'title_card') {
      renderTitleCard(ctx, width, height);
    } else if (previewStyle === 'end_crawl') {
      renderEndCrawl(ctx, width, height);
    } else {
      renderCustomStyle(ctx, width, height);
    }
  };

  const renderTitleCard = (ctx, width, height) => {
    if (credits?.length === 0) {
      ctx.fillStyle = customization?.textColor;
      ctx.font = `${customization?.fontSize}px ${customization?.font}`;
      ctx?.fillText('No credits to preview', width / 2, height / 2);
      return;
    }

    const creditIndex = currentFrame % credits?.length;
    const credit = credits?.[creditIndex];

    // Role title
    ctx.font = `${customization?.fontSize * 0.8}px ${customization?.font}`;
    ctx?.fillText(credit?.role?.toUpperCase()?.replace('_', ' '), width / 2, height / 2 - 40);

    // Name
    ctx.font = `${customization?.fontSize * 1.2}px ${customization?.font}`;
    ctx?.fillText(credit?.name, width / 2, height / 2);

    // Character (if applicable)
    if (credit?.character) {
      ctx.font = `${customization?.fontSize * 0.9}px ${customization?.font}`;
      ctx?.fillText(`as ${credit?.character}`, width / 2, height / 2 + 40);
    }
  };

  const renderEndCrawl = (ctx, width, height) => {
    if (credits?.length === 0) {
      ctx.fillStyle = customization?.textColor;
      ctx.font = `${customization?.fontSize}px ${customization?.font}`;
      ctx?.fillText('No credits to preview', width / 2, height / 2);
      return;
    }

    const lineHeight = customization?.fontSize * customization?.lineHeight;
    const startY = height + (currentFrame * customization?.animationSpeed * 2);

    credits?.forEach((credit, index) => {
      const y = startY - (index * lineHeight * 2);
      
      if (y > -lineHeight && y < height + lineHeight) {
        // Role
        ctx.font = `${customization?.fontSize * 0.8}px ${customization?.font}`;
        ctx?.fillText(credit?.role?.toUpperCase()?.replace('_', ' '), width / 2, y - lineHeight / 2);

        // Name
        ctx.font = `${customization?.fontSize}px ${customization?.font}`;
        ctx?.fillText(credit?.name, width / 2, y);

        // Character
        if (credit?.character) {
          ctx.font = `${customization?.fontSize * 0.8}px ${customization?.font}`;
          ctx?.fillText(credit?.character, width / 2, y + lineHeight / 2);
        }
      }
    });
  };

  const renderCustomStyle = (ctx, width, height) => {
    // Render based on selected style
    if (previewStyle === 'modern_minimal') {
      ctx.fillStyle = '#F7FAFC';
      ctx?.fillRect(0, 0, width, height);
      ctx.fillStyle = '#1A202C';
    } else if (previewStyle === 'classic_hollywood') {
      ctx.fillStyle = '#000000';
      ctx?.fillRect(0, 0, width, height);
      ctx.fillStyle = '#FFD700';
    }

    renderTitleCard(ctx, width, height);
  };

  const togglePlayback = () => {
    setIsPlaying(!isPlaying);
  };

  const resetPreview = () => {
    setCurrentFrame(0);
    setIsPlaying(false);
  };

  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentFrame(prev => prev + 1);
      }, 100 / customization?.animationSpeed);
    }
    return () => clearInterval(interval);
  }, [isPlaying, customization?.animationSpeed]);

  return (
    <div className="space-y-6">
      {/* Style Selection */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Preview Style</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Credit Style"
            options={styleOptions}
            value={previewStyle}
            onChange={onStyleChange}
          />
          <Select
            label="Font Family"
            options={fontOptions}
            value={customization?.font}
            onChange={(value) => setCustomization(prev => ({ ...prev, font: value }))}
          />
        </div>
      </div>
      {/* Canvas Preview */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Live Preview</h3>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName={isPlaying ? "Pause" : "Play"}
              onClick={togglePlayback}
            />
            <Button
              variant="outline"
              size="sm"
              iconName="RotateCcw"
              onClick={resetPreview}
            />
          </div>
        </div>

        <div className="relative bg-black rounded-lg overflow-hidden">
          <canvas
            ref={canvasRef}
            width={800}
            height={450}
            className="w-full h-auto max-h-96"
          />
          
          {credits?.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80">
              <div className="text-center text-white">
                <Icon name="Film" size={48} className="mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No Credits to Preview</p>
                <p className="text-sm opacity-75">Add credits to see the preview</p>
              </div>
            </div>
          )}
        </div>

        {/* Preview Controls */}
        <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
          <span>Frame: {currentFrame}</span>
          <span>{credits?.length} credits total</span>
        </div>
      </div>
      {/* Customization Options */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Customization</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Font Size</label>
            <input
              type="range"
              min="16"
              max="48"
              value={customization?.fontSize}
              onChange={(e) => setCustomization(prev => ({ ...prev, fontSize: parseInt(e?.target?.value) }))}
              className="w-full"
            />
            <span className="text-xs text-muted-foreground">{customization?.fontSize}px</span>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Line Height</label>
            <input
              type="range"
              min="1"
              max="3"
              step="0.1"
              value={customization?.lineHeight}
              onChange={(e) => setCustomization(prev => ({ ...prev, lineHeight: parseFloat(e?.target?.value) }))}
              className="w-full"
            />
            <span className="text-xs text-muted-foreground">{customization?.lineHeight}x</span>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Animation Speed</label>
            <input
              type="range"
              min="0.5"
              max="3"
              step="0.1"
              value={customization?.animationSpeed}
              onChange={(e) => setCustomization(prev => ({ ...prev, animationSpeed: parseFloat(e?.target?.value) }))}
              className="w-full"
            />
            <span className="text-xs text-muted-foreground">{customization?.animationSpeed}x</span>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Text Color</label>
            <input
              type="color"
              value={customization?.textColor}
              onChange={(e) => setCustomization(prev => ({ ...prev, textColor: e?.target?.value }))}
              className="w-full h-10 rounded border border-border"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Background Color</label>
            <input
              type="color"
              value={customization?.backgroundColor}
              onChange={(e) => setCustomization(prev => ({ ...prev, backgroundColor: e?.target?.value }))}
              className="w-full h-10 rounded border border-border"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditPreview;